import { Text, Table } from "@postenbring/hedwig-react";

import {
    SERVICE_POSTBOXDELIVERY,
    SERVICE_HOMEDELIVERYPACKAGE,
    SERVICE_PICKUPPOINTDELIVERY,
    SERVICE_EXPRESSNEXTDAYDELIVERY,
    SERVICE_BUSINESSPARCEL,
    SERVICE_BUSINESSGROUPAGE,
    WEIGHTUNIT_FACTORS,
} from "../price-calculator/constants";
import { usePriceMatrix } from "../price-calculator/hooks/usePriceMatrix";
import { buildTableData } from "../price-calculator/resultFeedback/priceMatrix/priceTable";

import { MatrixData as postboxDelivery } from "../price-calculator/resultFeedback/priceMatrix/postboxDelivery/3584";
import { MatrixData as homeDeliveryPackage } from "../price-calculator/resultFeedback/priceMatrix/homeDeliveryPackage/5600";
import { MatrixData as pickUpPointDelivery } from "../price-calculator/resultFeedback/priceMatrix/pickUpPointDelivery/5800";
import { MatrixData as expressNextDayDelivery } from "../price-calculator/resultFeedback/priceMatrix/expressNextDayDelivery/4850";
import { MatrixData as businessParcel } from "../price-calculator/resultFeedback/priceMatrix/businessParcel/5000";
import { MatrixData as businessGroupage } from "../price-calculator/resultFeedback/priceMatrix/businessGroupage/5100";

const DEFAULT_MAX_ZONECOLUMNS_TO_DISPLAY = 18;
const DEFAULT_MAX_WEIGHTROWS_TO_DISPLAY = 30;
const STANDALONE_WIDTH = 0;
const STANDALONE_LENGTH = 0;
const STANDALONE_HEIGHT = 0;
const STANDALONE_PARCELS = 1;
const STANDALONE_DATA = null;
const STANDALONE_ZONE = null;

const SERVICE_MATRIXDATA = {
    [SERVICE_POSTBOXDELIVERY]: postboxDelivery,
    [SERVICE_HOMEDELIVERYPACKAGE]: homeDeliveryPackage,
    [SERVICE_PICKUPPOINTDELIVERY]: pickUpPointDelivery,
    [SERVICE_EXPRESSNEXTDAYDELIVERY]: expressNextDayDelivery,
    [SERVICE_BUSINESSPARCEL]: businessParcel,
    [SERVICE_BUSINESSGROUPAGE]: businessGroupage,
};

const SERVICEID_MATRIXDATA = {
    [postboxDelivery.serviceId]: postboxDelivery,
    [homeDeliveryPackage.serviceId]: homeDeliveryPackage,
    [pickUpPointDelivery.serviceId]: pickUpPointDelivery,
    [expressNextDayDelivery.serviceId]: expressNextDayDelivery,
    [businessParcel.serviceId]: businessParcel,
    [businessGroupage.serviceId]: businessGroupage,
};

const resolveMatrixData = (service) => {
    if (!service) {
        return undefined;
    }

    if (SERVICE_MATRIXDATA[service]) {
        return SERVICE_MATRIXDATA[service];
    }

    const serviceId = String(service);
    return SERVICEID_MATRIXDATA[serviceId];
};

export const PriceMatrixStandalone = ({
    labels,
    service,
    priceApiUrl,
    maxZoneColumnsToDisplay = DEFAULT_MAX_ZONECOLUMNS_TO_DISPLAY,
    maxWeightRowsToDisplay = DEFAULT_MAX_WEIGHTROWS_TO_DISPLAY,
}) => {
    const matrixData = resolveMatrixData(service);
    const serviceId = matrixData?.serviceId;

    const {
        matrixData: matrixDataFromApi,
        loading,
        error,
    } = usePriceMatrix(serviceId, priceApiUrl);

    if (!matrixData) {
        return null;
    }

    if (loading) {
        return <Text variant="body">{labels.loading}</Text>;
    }

    if (error || !matrixDataFromApi?.tableData?.prices?.length) {
        return <Text variant="body">{labels.loadingError}</Text>;
    }

    const weightFactor = WEIGHTUNIT_FACTORS[matrixData.weightUnit];
    if (!weightFactor) {
        throw Error("matrixData.weightUnit is missing or invalid");
    }

    // API returns live table prices/zones, while static metadata holds rules needed by buildTableData.
    const matrixDataForTable = {
        ...matrixData,
        ...matrixDataFromApi,
        tableData: matrixDataFromApi.tableData,
    };

    const tableData = buildTableData(
        matrixDataForTable,
        matrixData.weights?.[0],
        weightFactor,
        STANDALONE_WIDTH,
        STANDALONE_LENGTH,
        STANDALONE_HEIGHT,
        STANDALONE_PARCELS,
        service,
        STANDALONE_DATA,
        STANDALONE_ZONE,
        maxZoneColumnsToDisplay,
        maxWeightRowsToDisplay,
    );

    const isValidTableData =
        tableData &&
        typeof tableData === "object" &&
        Array.isArray(tableData.zoneHeadColumns) &&
        tableData.tablePriceRows &&
        typeof tableData.tablePriceRows === "object" &&
        !Array.isArray(tableData.tablePriceRows);

    if (!isValidTableData) {
        return <Text variant="body">{labels.loadingError}</Text>;
    }

    const { zoneHeadColumns, tablePriceRows } = tableData;

    return (
        <Table size="compressed">
            <colgroup>
                <col />
                {zoneHeadColumns.map((column, i) => (
                    <col key={i} />
                ))}
            </colgroup>
            <thead>
                <tr>
                    <th>{labels.matrixWeightHeader}</th>
                    {zoneHeadColumns.map((column, i) => (
                        <th key={i}>
                            {labels.zoneHeader} {`${column.zoneNumber}`}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Object.keys(tablePriceRows).map((weightKey, i) => {
                    const row = tablePriceRows[weightKey];
                    if (!Array.isArray(row)) {
                        return null;
                    }

                    return (
                        <tr key={i}>
                            <th>{weightKey * weightFactor}</th>
                            {row.map((cell, j) => (
                                <td key={j}>{cell?.price}</td>
                            ))}
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
};
